import { useState, useRef, useEffect } from "react";
import { ChatCircleDots, X, PaperPlaneTilt, Translate, Star, CaretDown, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const GREETINGS = {
  en: "Hi! I'm the {name} digital concierge. How can I help you today?",
  es: "¡Hola! Soy el conserje digital de {name}. ¿En qué puedo ayudarle hoy?",
  fr: "Bonjour ! Je suis le concierge numérique de {name}. Comment puis-je vous aider ?",
  de: "Hallo! Ich bin der digitale Concierge von {name}. Wie kann ich Ihnen helfen?",
  it: "Ciao! Sono il concierge digitale di {name}. Come posso aiutarla oggi?",
  pt: "Olá! Sou o concierge digital do {name}. Como posso ajudá-lo hoje?",
  zh: "您好！我是{name}的数字礼宾员。今天我能为您做些什么？",
  ja: "こんにちは！{name}のデジタルコンシェルジュです。本日はどのようなご用件でしょうか？",
  ar: "مرحباً! أنا مساعد الاستقبال الرقمي في {name}. كيف يمكنني مساعدتك اليوم؟",
};
const PLACEHOLDERS = {
  en: "Ask about breakfast, parking…", es: "Pregunte por el desayuno, parking…",
  fr: "Petit-déjeuner, parking…", de: "Frühstück, Parken…", it: "Colazione, parcheggio…",
  pt: "Café da manhã, estacionamento…", zh: "咨询早餐、停车…", ja: "朝食や駐車場について…",
  ar: "اسأل عن الإفطار، موقف السيارات…",
};
const SURVEY_THANKS = {
  en: "Thank you for your feedback!", es: "¡Gracias por sus comentarios!",
  fr: "Merci pour votre retour !", de: "Vielen Dank für Ihr Feedback!",
  it: "Grazie per il suo riscontro!", pt: "Obrigado pelo seu feedback!",
  zh: "感谢您的反馈！", ja: "ご意見ありがとうございます！", ar: "شكراً على ملاحظاتك!",
};
const BOOKING_LABELS = {
  en: { summary: "Booking summary", checkin: "Check-in", checkout: "Check-out", nights: "nights", night: "night", guests: "Guests", perNight: "per night", total: "Total", confirm: "Confirm booking" },
  es: { summary: "Resumen de reserva", checkin: "Entrada", checkout: "Salida", nights: "noches", night: "noche", guests: "Huéspedes", perNight: "por noche", total: "Total", confirm: "Confirmar reserva" },
  fr: { summary: "Récapitulatif", checkin: "Arrivée", checkout: "Départ", nights: "nuits", night: "nuit", guests: "Voyageurs", perNight: "par nuit", total: "Total", confirm: "Confirmer la réservation" },
  de: { summary: "Buchungsübersicht", checkin: "Anreise", checkout: "Abreise", nights: "Nächte", night: "Nacht", guests: "Gäste", perNight: "pro Nacht", total: "Gesamt", confirm: "Buchung bestätigen" },
  it: { summary: "Riepilogo prenotazione", checkin: "Check-in", checkout: "Check-out", nights: "notti", night: "notte", guests: "Ospiti", perNight: "a notte", total: "Totale", confirm: "Conferma prenotazione" },
  pt: { summary: "Resumo da reserva", checkin: "Check-in", checkout: "Check-out", nights: "noites", night: "noite", guests: "Hóspedes", perNight: "por noite", total: "Total", confirm: "Confirmar reserva" },
  zh: { summary: "预订摘要", checkin: "入住", checkout: "退房", nights: "晚", night: "晚", guests: "客人", perNight: "每晚", total: "合计", confirm: "确认预订" },
  ja: { summary: "予約内容", checkin: "チェックイン", checkout: "チェックアウト", nights: "泊", night: "泊", guests: "人数", perNight: "1泊あたり", total: "合計", confirm: "予約を確定する" },
  ar: { summary: "ملخص الحجز", checkin: "تسجيل الوصول", checkout: "تسجيل المغادرة", nights: "ليالٍ", night: "ليلة", guests: "الضيوف", perNight: "لكل ليلة", total: "الإجمالي", confirm: "تأكيد الحجز" },
};

const HANDOFF_BANNER = {
  en: "You're now chatting with our front desk team", es: "Ahora está hablando con nuestro equipo de recepción",
  fr: "Vous discutez maintenant avec notre réception", de: "Sie chatten jetzt mit unserem Empfangsteam",
  it: "Ora sta parlando con il nostro team della reception", pt: "Agora você está falando com a nossa recepção",
  zh: "您正在与我们的前台团队对话", ja: "現在フロントデスクチームとチャット中です",
  ar: "أنت الآن تتحدث مع فريق مكتب الاستقبال",
};
const FRONT_DESK = {
  en: "Front desk", es: "Recepción", fr: "Réception", de: "Empfang", it: "Reception",
  pt: "Recepção", zh: "前台", ja: "フロントデスク", ar: "مكتب الاستقبال",
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(lang || "en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(`${String(iso).slice(0, 10)}T00:00:00`));
  } catch { return iso; }
};
const fmtMoney = (n, cur) => (n === null || n === undefined ? "" : `${cur || "$"}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

function BookingCard({ booking, brandColor, lang }) {
  const L = BOOKING_LABELS[lang] || BOOKING_LABELS.en;
  const nightsLabel = booking.nights === 1 ? L.night : L.nights;
  return (
    <div data-testid="widget-booking-card" className="mt-2 rounded-xl border border-border bg-card overflow-hidden shadow-sm">
      <div className="px-3.5 py-2.5 text-white text-[12px] font-heading font-semibold tracking-wide" style={{ background: brandColor }}>
        {L.summary}
      </div>
      <div className="p-3.5 space-y-2 text-[12.5px]">
        {booking.room && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Room</span>
            <span data-testid="booking-card-room" className="font-medium text-foreground">{booking.room}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{L.checkin}</span>
          <span data-testid="booking-card-checkin" className="font-medium text-foreground">{fmtDate(booking.arrival_date, lang)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{L.checkout}</span>
          <span data-testid="booking-card-checkout" className="font-medium text-foreground">{fmtDate(booking.departure_date, lang)}</span>
        </div>
        {(booking.nights || booking.adults) && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{L.guests}</span>
            <span className="font-medium text-foreground">
              {booking.adults ? booking.adults : ""}{booking.adults ? " · " : ""}{booking.nights ? `${booking.nights} ${nightsLabel}` : ""}
            </span>
          </div>
        )}
        {booking.price_per_night != null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{fmtMoney(booking.price_per_night, booking.currency)} {L.perNight}</span>
            {booking.total != null && <span className="text-foreground font-semibold">{L.total}: {fmtMoney(booking.total, booking.currency)}</span>}
          </div>
        )}
        <a
          data-testid="booking-card-confirm"
          href={booking.booking_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-[13px] font-semibold text-white hover:opacity-90 active:scale-[0.98] transition"
          style={{ background: brandColor }}
        >
          {L.confirm}
        </a>
      </div>
    </div>
  );
}

const greet = (lang, name) => (GREETINGS[lang] || GREETINGS.en).replace("{name}", name);

export function ChatWidget({ propertyId, brandColor = "#EA580C", propertyName, languages = [], survey = null, embed = false, title = "", welcomeMessage = "", avatar = "" }) {
  const auth = useAuth();
  const pid = propertyId || auth?.property?.id;
  const name = propertyName || auth?.property?.name || "our hotel";
  const headerTitle = title || name;
  const welcome = (code) => welcomeMessage || greet(code, name);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [langOpen, setLangOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([{ role: "ai", text: welcomeMessage || greet("en", name) }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [surveyDone, setSurveyDone] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);
  const [agentMode, setAgentMode] = useState("ai");
  const scrollRef = useRef(null);

  const isRTL = lang === "ar";
  const langList = languages.length ? languages : [{ code: "en", native: "English" }];
  const surveyQ = survey ? (lang === "en" ? survey.question : (survey.translations?.[lang] || survey.question)) : null;

  const notifyParent = (type) => {
    if (embed && typeof window !== "undefined" && window.parent) {
      window.parent.postMessage({ type }, "*");
    }
  };
  const openWidget = () => { setOpen(true); notifyParent("guestivate:open"); };
  const closeWidget = () => { setOpen(false); notifyParent("guestivate:close"); };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, surveyDone]);

  const syncConv = async (cid) => {
    const id = cid || convId;
    if (!id || !pid) return;
    try {
      const { data } = await api.get(`/widget/${pid}/conversation/${id}`);
      const mapped = (data.messages || []).map((m) => ({
        role: m.role, text: m.text, booking: m.booking, service: m.service, escalated: m.escalated,
      }));
      setMessages([{ role: "ai", text: welcome(lang) }, ...mapped]);
      setAgentMode(data.agent_mode || "ai");
    } catch { /* silent */ }
  };

  // Poll for live staff replies / handoff while the chat is open.
  useEffect(() => {
    if (!open || !convId) return;
    const t = setInterval(() => syncConv(convId), 4000);
    return () => clearInterval(t);
  }, [open, convId, pid, lang, name]);

  const changeLang = (code) => {
    setLang(code);
    setLangOpen(false);
    // reset greeting to the newly-chosen language when it's the only message so far
    setMessages((m) => (m.length === 1 && m[0].role === "ai" ? [{ role: "ai", text: welcome(code) }] : m));
  };

  const send = async () => {
    if (!input.trim() || busy || !pid) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "guest", text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/widget/${pid}/chat`, { message: text, conversation_id: convId, lang });
      setConvId(data.conversation_id);
      if (data.agent_mode) setAgentMode(data.agent_mode);
      // Rebuild from the server so AI replies, booking cards and any staff messages stay in sync.
      await syncConv(data.conversation_id);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const submitSurvey = async (score) => {
    if (!pid) return;
    try {
      await api.post(`/widget/${pid}/survey`, { score, language: lang, comment: "" });
      setSurveyDone(true);
    } catch { /* silent */ }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-widget-toggle"
          onClick={openWidget}
          className={`fixed z-[60] h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-transform duration-150 ${embed ? "bottom-3 right-3" : "bottom-24 right-6"}`}
          style={{ background: brandColor }}
          aria-label="Open concierge chat"
        >
          <ChatCircleDots size={26} weight="fill" />
        </button>
      )}

      {open && (
        <div dir={isRTL ? "rtl" : "ltr"} className={`fixed z-50 rounded-xl border border-border bg-card shadow-2xl overflow-hidden flex flex-col ${embed ? "bottom-3 right-3 w-[calc(100vw-24px)] max-w-[384px]" : "bottom-6 right-6 w-[356px] max-w-[calc(100vw-3rem)]"}`} style={{ height: embed ? "min(600px, calc(100vh - 24px))" : 540 }}>
          <div className="flex items-center justify-between px-4 py-3.5 text-white" style={{ background: brandColor }}>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {avatar
                  ? <img src={avatar} alt="" className="h-8 w-8 object-cover" />
                  : <ChatCircleDots size={18} weight="fill" />}
              </div>
              <div>
                <div className="font-heading font-semibold text-sm leading-none">{headerTitle}</div>
                <div className="text-[11px] opacity-90 mt-1 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-300 pulse-dot inline-block" /> {agentMode === "human" ? `${FRONT_DESK[lang] || FRONT_DESK.en} · Live` : "AI Concierge · Online"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {/* language switcher */}
              <div className="relative">
                <button
                  data-testid="widget-lang-toggle"
                  onClick={() => setLangOpen((o) => !o)}
                  className="flex items-center gap-1 text-[11px] font-medium bg-white/15 hover:bg-white/25 rounded-full pl-2 pr-1.5 py-1 transition-colors"
                >
                  <Translate size={13} weight="bold" />
                  {(langList.find((l) => l.code === lang) || {}).native || "EN"}
                  <CaretDown size={10} weight="bold" />
                </button>
                {langOpen && (
                  <div data-testid="widget-lang-menu" className="absolute right-0 mt-1.5 w-40 max-h-64 overflow-y-auto rounded-lg bg-card border border-border shadow-xl py-1 z-10">
                    {langList.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`widget-lang-${l.code}`}
                        onClick={() => changeLang(l.code)}
                        className={`w-full text-left px-3 py-1.5 text-[13px] text-foreground hover:bg-secondary transition-colors ${l.code === lang ? "font-semibold" : ""}`}
                      >
                        {l.native}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="chat-widget-close" onClick={closeWidget} className="opacity-90 hover:opacity-100">
                <X size={20} />
              </button>
            </div>
          </div>

          {agentMode === "human" && (
            <div data-testid="widget-handoff-banner" className="px-3.5 py-2 text-[11.5px] font-medium text-center border-b border-border" style={{ background: `${brandColor}14`, color: brandColor }}>
              {HANDOFF_BANNER[lang] || HANDOFF_BANNER.en}
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3.5 space-y-3 bg-secondary/30">
            {messages.map((m, i) => {
              if (m.role === "system") {
                return (
                  <div key={i} className="flex justify-center">
                    <span className="text-[11px] text-muted-foreground bg-card border border-border rounded-full px-3 py-1">{m.text}</span>
                  </div>
                );
              }
              const isGuest = m.role === "guest";
              const isStaff = m.role === "staff";
              return (
                <div key={i} className={`flex flex-col ${isGuest ? "items-end" : "items-start"}`}>
                  <div
                    className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                      isGuest
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : isStaff
                        ? "bg-foreground text-background rounded-bl-md"
                        : "bg-card border border-border rounded-bl-md"
                    }`}
                  >
                    {isStaff && <div className="text-[10px] font-semibold opacity-80 mb-0.5">{FRONT_DESK[lang] || FRONT_DESK.en}</div>}
                    {m.text}
                    {m.service?.created?.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid="widget-service-tickets">
                        {m.service.created.map((t) => (
                          <div key={t.id} className="flex items-center gap-1.5 text-[11.5px] text-success">
                            <CheckCircle size={13} weight="fill" /> {t.quantity > 1 ? `${t.quantity}× ` : ""}{t.request_type}
                          </div>
                        ))}
                      </div>
                    )}
                    {m.escalated && (
                      <div className="mt-1.5 text-[11px] font-medium text-amber-600">↗ Escalated to front desk</div>
                    )}
                  </div>
                  {m.booking && (
                    <div className="w-[90%] max-w-[300px]">
                      <BookingCard booking={m.booking} brandColor={brandColor} lang={lang} />
                    </div>
                  )}
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-card border border-border rounded-2xl rounded-bl-md px-3.5 py-3 flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground pulse-dot" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground pulse-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground pulse-dot" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}

            {/* multilingual quick survey */}
            {surveyQ && (
              <div data-testid="widget-survey" className="bg-card border border-border rounded-xl p-3.5 mt-1">
                {surveyDone ? (
                  <div className="text-[13px] text-success font-medium flex items-center gap-1.5">
                    <Star size={15} weight="fill" /> {SURVEY_THANKS[lang] || SURVEY_THANKS.en}
                  </div>
                ) : (
                  <>
                    <div className="text-[13px] font-medium mb-2">{surveyQ}</div>
                    <div className="flex items-center gap-1" onMouseLeave={() => setHoverStar(0)}>
                      {Array.from({ length: survey.scale || 5 }).map((_, j) => (
                        <button
                          key={j}
                          data-testid={`widget-survey-star-${j + 1}`}
                          onMouseEnter={() => setHoverStar(j + 1)}
                          onClick={() => submitSurvey(j + 1)}
                          className="text-amber-400 hover:scale-110 transition-transform"
                        >
                          <Star size={22} weight={j < hoverStar ? "fill" : "regular"} />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-border bg-card flex items-center gap-2">
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={PLACEHOLDERS[lang] || PLACEHOLDERS.en}
              className="flex-1 text-[13px] px-3 py-2 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              data-testid="chat-widget-send"
              onClick={send}
              disabled={busy}
              className="h-9 w-9 rounded-md flex items-center justify-center text-white disabled:opacity-50 transition-transform active:scale-95"
              style={{ background: brandColor }}
            >
              <PaperPlaneTilt size={17} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
